import { Action } from 'redux';
import * as actions from './actions';
import { TracPage } from '@services/tracpage/types/trac-page';
import { LoadingAction } from '../common/loading-action';
import { ErrorAction } from '../common/error-action';
import TracLink from '@services/tracpage/types/trac-link';
import InputTracPage from '../../../../../services/tracpage/types/db/input-trac-page';
import { StoredTracPageEdits } from './effects/store-tracpage-edits';

export class PublishTracPageDraft implements LoadingAction {
  readonly type = actions.PUBLISH_TRAC_PAGE_DRAFT;
  readonly isLoading = true;
  readonly mainLoader = true;
}

export class PublishTracPageDraftSuccess implements LoadingAction {
  readonly type = actions.PUBLISH_TRAC_PAGE_DRAFT_SUCCESS;
  readonly isLoading = false;
  readonly mainLoader = false;
}

export class UpdateTracPageField<
  T extends TracPage | TracLink,
  U extends keyof T
> implements Action
{
  readonly type = actions.UPDATE_TRAC_PAGE_FIELD;

  constructor(
    public pageType: T['type'],
    public field: U,
    public value: T[U],
    public config?: Partial<{
      noAutosave: boolean;
      updateDraft: boolean;
    }>
  ) {}
}

export class UpdateTracPageDataField<
  T extends TracPage | TracLink,
  U extends keyof T['data']
> implements Action
{
  readonly type = actions.UPDATE_TRAC_PAGE_DATA_FIELD;

  constructor(
    public pageType: T['type'],
    public field: U,
    public value: T['data'][U],
    public config?: Partial<{
      updateDraft: boolean;
    }>
  ) {}
}

export class GetTracPage implements LoadingAction {
  readonly type = actions.GET_TRAC_PAGE;
  readonly isLoading = true;
  readonly mainLoader = true;
}

export class GetTracPageSuccess implements LoadingAction {
  readonly type = actions.GET_TRAC_PAGE_SUCCESS;
  readonly isLoading = false;

  constructor(
    public tracPage: TracPage,
    public draft: StoredTracPageEdits | null
  ) {}
}

export class GetTracPageError implements ErrorAction {
  readonly type = actions.GET_TRAC_PAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class DeleteTracLink implements LoadingAction {
  readonly type = actions.DELETE_TRAC_LINK;
  readonly isLoading = true;

  constructor(public tracLinkId: string, public title: string) {}
}

export class DeleteTracLinkSuccess implements LoadingAction {
  readonly type = actions.DELETE_TRAC_LINK_SUCCESS;
  readonly isLoading = false;

  constructor(public tracLinkId: string) {}
}

export class DeleteTracLinkError implements ErrorAction {
  readonly type = actions.DELETE_TRAC_LINK_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class CreateTracLink implements LoadingAction {
  readonly type = actions.CREATE_TRAC_LINK;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(public tracLink: InputTracPage<TracLink>) {}
}

export class CreateTracLinkSuccess implements LoadingAction {
  readonly type = actions.CREATE_TRAC_LINK_SUCCESS;
  readonly isLoading = false;

  constructor(public tracLink: TracLink) {}
}

export class CreateTracLinkError implements ErrorAction {
  readonly type = actions.CREATE_TRAC_LINK_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ListTracLinks implements LoadingAction {
  readonly type = actions.LIST_TRAC_LINKS;
  readonly isLoading = true;
  readonly mainLoader = true;
}

export class ListTracLinksSuccess implements LoadingAction {
  readonly type = actions.LIST_TRAC_LINKS_SUCCESS;
  readonly isLoading = false;

  constructor(public tracLinks: TracLink[]) {}
}

export class ListTracLinksError implements ErrorAction {
  readonly type = actions.LIST_TRAC_LINKS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateTracPage<T extends TracPage | TracLink>
  implements LoadingAction
{
  readonly type = actions.UPDATE_TRAC_PAGE;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(
    public pageType: T['type'],
    public tracPage: T,
    public disableAutoSave?: boolean
  ) {}
}

export class UpdateTracPageSuccess<T extends TracPage | TracLink>
  implements LoadingAction
{
  readonly type = actions.UPDATE_TRAC_PAGE_SUCCESS;
  readonly isLoading = false;
  readonly mainLoader = false;

  constructor(public pageType: T['type'], public tracPage: T) {}
}

export class UpdateTracPageError implements ErrorAction {
  readonly type = actions.UPDATE_TRAC_PAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class SelectTracLink implements Action {
  readonly type = actions.SELECT_TRAC_LINK;

  constructor(public tracLink: TracLink) {}
}
export class SetArtistPreview implements Action {
  readonly type = actions.SET_ARTIST_PREVIEW;

  constructor(public value: boolean) {}
}
export type TracPageActions =
  | GetTracPage
  | GetTracPageSuccess
  | GetTracPageError
  | DeleteTracLink
  | DeleteTracLinkSuccess
  | DeleteTracLinkError
  | ListTracLinks
  | ListTracLinksSuccess
  | ListTracLinksError
  | CreateTracLink
  | CreateTracLinkSuccess
  | CreateTracLinkError
  | UpdateTracPage<TracPage | TracLink>
  | UpdateTracPageSuccess<TracPage | TracLink>
  | UpdateTracPageError
  | UpdateTracPageField<
      TracPage | TracLink,
      keyof (TracPage | TracLink)['data']
    >
  | UpdateTracPageDataField<
      TracPage | TracLink,
      keyof (TracPage | TracLink)['data']
    >
  | SelectTracLink
  | SetArtistPreview
  | PublishTracPageDraft
  | PublishTracPageDraftSuccess;
